import React, { useEffect } from 'react'
import { ElementProps } from '../base/element'
import {
  IComponent,
  IDataComponent,
  IDefaultStyle,
  IDefaultStyles,
  IStringArrayFunction,
  IStyles
} from '../schemamanager/types'
import { Dropdown } from '../helpers/dropdown'
import { Chips } from '../helpers/chips'
import debounce from 'lodash.debounce'
import { EventType } from '../schemamanager/schemaManager'

export interface IInputComponent extends IDataComponent {
  type: 'input'
  multiline?: boolean
  multiselect?: boolean
  placeholder?: string
  options?: string[] | IStringArrayFunction
  styleInput?: IStyles
  styleError?: IStyles
  styleDisabled?: IStyles
}

export const Input = (props: ElementProps) => {
  const { sm, themeColor, events, data, addProps, validateOnBlur } = props
  const comp = props.comp as IInputComponent

  const options: string[] = sm.selectOptionsAsStrings(comp)

  const getDefaultEditValue = () => {
    if (comp.multiselect) {
      return ''
    } else {
      return data.value()
    }
  }

  const getDefaultChipsValue = (): string[] => {
    if (comp.multiselect) {
      return data.value() || []
    } else {
      return []
    }
  }

  const ref: any = React.useRef()
  const leaveDropDownOnBlur = React.useRef<boolean>(false)

  const [editValue, setEditValue] = React.useState(getDefaultEditValue())
  const [chipsArray, setChipsArray] = React.useState<string[]>(
    getDefaultChipsValue()
  )
  const [filteredOptions, setFilteredOptions] = React.useState<string[]>([])

  const doFocus = (compToFocus: IComponent) => {
    if (compToFocus['field'] === comp.field) {
      ref.current?.focus()
    }
  }

  useEffect(() => {
    sm.on(EventType.FocusComponent, doFocus)
    return () => {
      sm.removeListener(EventType.FocusComponent, doFocus)
    }
  }, [sm])

  const style: IDefaultStyles = {
    main: {
      classes: {
        margin: 'mx-2',
        padding: 'px-3 py-2',
        boxShadow: 'shadow',
        borderWidth: 'border',
        borderRadius: 'rounded',
        lineHeight: 'leading-tight',
        focus: {
          boxShadow: 'focus:shadow-outline',
          outline: 'focus:outline-none'
        }
      },
      class: themeColor().focusWiBorderColor,
      // style: { gridTemplateColumns: 'fit-content(70%) 1fr' }
    },
    styleInput: {
      classes: {
        gridColumnStartEnd: 'col-start-2',
        width: 'w-full',
        textColor: 'text-gray-700',
        borderWidth: 'border-0',
        appearance: 'appearance-none',
        padding: 'py-1 px-4 pr-8',
        focus: {
          outline: 'focus:outline-none',
          borderWidth: 'focus:border-0',
          appearance: 'focus:appearance-none',
          boxShadow: 'focus:shadow-none',
          ringWidth: 'focus:ring-0'
        }
      },
      class: 'disabled:bg-gray-50'
    },
    styleDisabled: {
      class: 'bg-gray-50'
    }
  }

  const styleError: IDefaultStyle = {
    classes: {
      ...style.main.classes,
      borderColor: 'border-red-500'
    }
  }

  const styleMultiselect: IDefaultStyle = {
    classes: {
      ...style.main.classes,
      display: 'grid',
    },
    style: { gridTemplateColumns: 'fit-content(70%) 1fr' }    
  }


  const triggerOptions = debounce(() => {
    if (options) {
      const v = ref.current?.value || ''.toLowerCase()
      const _filteredOptions = options.filter((o) => {
        const ol = o.toLowerCase()
        return v && ol !== v && o.toLowerCase().indexOf(v) > -1
      })
      setFilteredOptions(_filteredOptions)
    }
  }, 400)

  const handleChange = (_editValue: any, _chipsArray: string[]) => {
    setEditValue(_editValue)
    let fullValue: any = _editValue
    if (comp.multiselect) {
      const arr: string[] = _chipsArray || []
      if (_editValue) {
        fullValue = [...arr, _editValue]
      } else {
        fullValue = arr
      }
      setChipsArray([..._chipsArray])
    }
    data.updateValue(fullValue)
    if (options) {
      triggerOptions()
    }

    if (events().onChange) {
      events().onChange({ sm, comp, value: fullValue })
    }
  }

  const onDropdownItemSelected = (value: string) => {
    // console.log('onDropdownItemSelected')
    if (value) {
      if (comp.multiselect) {
        addChip(value)
      } else {
        handleChange(value, chipsArray)
      }
    }
    ref.current?.focus()
  }

  const onDropDownEscKey = () => {
    setFilteredOptions([])
    ref.current?.focus()
  }

  const addChip = (value?: string) => {
    if (typeof value === 'undefined') {
      value = ref.current?.value
    }
    if (value) {
      if (chipsArray.indexOf(value) === -1) {
        chipsArray.push(value)
      }
      handleChange('', [...chipsArray])
    }
  }

  const deleteLastChip = () => {
    if (!editValue && chipsArray && chipsArray.length > 0) {
      chipsArray.pop()
      handleChange(editValue, [...chipsArray])
    }
  }

  const onDeleteChip = (chip: string) => {
    const newChips: string[] = chipsArray.filter((i) => i !== chip)
    handleChange(editValue, newChips)
  }

  const onDropChip = (chips: string[]) => {
    handleChange(editValue, chips)
  }

  const handleKeyDown = (event: any) => {
    event.stopPropagation()
    // console.log(event.keyCode)
    leaveDropDownOnBlur.current = false
    if (options) {
      // arrow key down
      if (event.keyCode === 40) {
        if (!filteredOptions || filteredOptions.length === 0) {
          setFilteredOptions(options.map((o) => o))
        } else {
          leaveDropDownOnBlur.current = true
          sm.FocusDropDown(comp)
        }
        // esc key
      } else if (event.keyCode === 27) {
        setFilteredOptions([])
      }
    }
    if (comp.multiselect) {
      // return key
      if (event.keyCode === 13) {
        addChip()
        // backspace key
      } else if (event.keyCode === 8) {
        deleteLastChip()
      }
    }

    if (events().onKeyDown) {
      events().onKeyDown({ sm, comp, event })
    }
  }

  const handleBlur = () => {
    setTimeout(() => {
      validateOnBlur()
      if (
        !leaveDropDownOnBlur.current &&
        filteredOptions &&
        filteredOptions.length > 0
      ) {
        setFilteredOptions([])
      }
      if (comp.multiselect) {
        addChip()
      }
      leaveDropDownOnBlur.current = false
    }, 200)

  }

  const error = data.error()
  if (error) {
    style.main.classes = styleError.classes
  }

  if (comp.multiselect) {
    style.main.classes = styleMultiselect.classes
    style.main.style = styleMultiselect.style
  }

  let Inp = 'input'
  if (comp.multiline) {
    Inp = 'textarea'
  }
  const m = props.mergeStyles(style)

  const disabled = sm.IsCompDisabled(comp)
  if (disabled) {
    m.main.className = m.main.className + ' ' + m.styleDisabled.className
  }

  return (
    <div {...m.main}>
      {comp.multiselect && (
        <Chips
          chipsArray={chipsArray}
          onDeleteChip={onDeleteChip}
          onDrop={onDropChip}
          disabled={disabled}
        />
      )}
      <div className={comp.multiselect || options ? 'relative' : ''}>
        <Inp
          ref={ref}
          type='text'
          placeholder={comp.placeholder}
          {...m.styleInput}
          {...events()}
          value={editValue}
          onChange={(ev: any) => handleChange(ev.target.value, chipsArray)}
          onBlur={handleBlur}
          onKeyDown={(e: any) => handleKeyDown(e)}
          {...addProps()}
        />
        {filteredOptions && filteredOptions.length > 0 && (
          <Dropdown
            sm={sm}
            comp={comp}
            items={filteredOptions}
            onItemSelected={onDropdownItemSelected}
            onEscKey={onDropDownEscKey}
          />
        )}
      </div>
    </div>
  )
}
